/* ==========================================================================
   SkinTalk.mx — Diagnóstico de piel (quiz) + generador de rutina
   --------------------------------------------------------------------------
   Custom element <skin-quiz>. Flujo:
     intro → 8 preguntas → análisis → captura de correo → rutina personalizada

   Motor de recomendación:
     - Reglas curadas por (objetivo × tipo de piel × nivel de rutina).
     - Los handles se resuelven EN VIVO contra /products/<handle>.js, así que
       precios, imágenes y disponibilidad siempre están al día. Si un producto
       está agotado, se cae automáticamente al siguiente de la lista.

   Para editar recomendaciones: ver el objeto RULES más abajo. Cada regla tiene
   tres niveles (esencial / completa / premium) y un texto "why" que explica al
   cliente por qué ESE producto es para ELLA. Ese texto es lo que cierra la venta.
   ========================================================================== */

(function () {
  'use strict';

  const Base = window.SkinTalkElement || HTMLElement;
  const STORAGE_KEY = 'skintalk_quiz_v1';

  /* ======================================================================
     1. PREGUNTAS
     ====================================================================== */

  const QUESTIONS = [
    {
      id: 'objetivo',
      eyebrow: 'Tu objetivo',
      title: '¿Qué te gustaría mejorar?',
      help: 'Elige lo que más te molesta hoy.',
      options: [
        { v: 'acne', emoji: '🎯', label: 'Granitos y brotes', desc: 'Acné, puntos negros, marcas' },
        { v: 'manchas', emoji: '🌤️', label: 'Manchas', desc: 'Melasma, sol, tono disparejo' },
        { v: 'arrugas', emoji: '⏳', label: 'Líneas y flacidez', desc: 'Arrugas, piel cansada' },
        { v: 'deshidratacion', emoji: '💧', label: 'Piel apagada', desc: 'Sin luz, tirante' },
        { v: 'rojeces', emoji: '🌿', label: 'Rojeces', desc: 'Se irrita con todo' },
        { v: 'poros', emoji: '✨', label: 'Grasa y poros', desc: 'Brillo, textura' },
      ],
    },
    {
      id: 'piel',
      eyebrow: 'Tu piel',
      title: '¿Cómo se siente tu piel a media tarde?',
      help: 'Es la forma más honesta de saber tu tipo de piel.',
      options: [
        { v: 'grasa', emoji: '💫', label: 'Brilla toda', desc: 'Piel grasa' },
        { v: 'mixta', emoji: '🔀', label: 'Brilla la zona T', desc: 'Piel mixta' },
        { v: 'seca', emoji: '🍂', label: 'Tirante', desc: 'Piel seca' },
        { v: 'normal', emoji: '☁️', label: 'Cómoda', desc: 'Piel normal' },
        { v: 'sensible', emoji: '🌡️', label: 'Roja o con ardor', desc: 'Piel sensible' },
      ],
    },
    {
      id: 'edad',
      eyebrow: 'Tu edad',
      title: '¿Cuántos años tienes?',
      help: 'Ajustamos las concentraciones y las texturas.',
      cols: 2,
      options: [
        { v: '18-24', label: '18 – 24' },
        { v: '25-34', label: '25 – 34' },
        { v: '35-44', label: '35 – 44' },
        { v: '45+', label: '45 o más' },
      ],
    },
    {
      id: 'actual',
      eyebrow: 'Hoy',
      title: '¿Qué usas hoy?',
      help: 'Para completar lo que te falta, no repetirte lo que ya tienes.',
      options: [
        { v: 'nada', emoji: '🚿', label: 'Nada o solo agua' },
        { v: 'basico', emoji: '🧴', label: 'Limpiador y crema' },
        { v: 'activos', emoji: '💧', label: 'Algún suero o tratamiento' },
        { v: 'completa', emoji: '⭐', label: 'Rutina completa con retinol o ácidos' },
      ],
    },
    {
      id: 'nivel',
      eyebrow: 'Tu rutina',
      title: '¿Qué tan completa la quieres?',
      help: 'Puedes empezar por lo esencial y crecer después.',
      manual: true,
      cta: 'Ver mi rutina',
      check: { v: 'embarazo', label: 'Estoy embarazada o lactando', desc: 'Quitamos los activos que no puedes usar' },
      options: [
        { v: 'basica', label: 'Básica · 4 pasos', desc: 'Lo que tu piel necesita sí o sí · desde $3,200' },
        { v: 'esencial', label: 'Esencial · 5 pasos', desc: 'La base + el activo para tu objetivo · desde $5,200' },
        { v: 'completa', label: 'Completa · 6 pasos', desc: 'Resultados sin volverlo un ritual · desde $8,900', tag: 'La más elegida', featured: true },
        { v: 'premium', label: 'Pro · 8 pasos', desc: 'Todo lo que tu piel puede aprovechar · desde $15,500' },
      ],
      footnote: 'Todas incluyen limpiador, vitamina C, crema y protector solar. Puedes empezar por la básica y crecer después.',
    },
  ];

  /* Orden fijo de las respuestas guardadas (incluye lo que no es una pregunta). */
  const ANSWER_KEYS = QUESTIONS.map((q) => q.id).concat(['notas']);

  /**
   * De 5 respuestas al perfil completo que consumen las reglas.
   * La experiencia con activos se deduce de lo que ya usa: es más fácil de
   * contestar y más confiable que preguntársela de frente.
   */
  const EXPERIENCIA_POR_USO = {
    nada: 'principiante',
    basico: 'principiante',
    activos: 'intermedio',
    completa: 'avanzado',
  };

  function profileOf(answers) {
    return Object.assign({}, answers, {
      experiencia: EXPERIENCIA_POR_USO[answers.actual] || 'intermedio',
      notas: answers.notas || [],
    });
  }

  /* ======================================================================
     2. ESTRUCTURA DE LA RUTINA
     ====================================================================== */

  /**
   * La BASE son siempre los mismos 4 pasos: limpiador, vitamina C, crema y
   * protector solar. Aparecen en cualquier rutina, sin importar el nivel.
   * El tratamiento del objetivo (manchas, líneas, flacidez…) es un paso EXTRA
   * que solo entra a partir del nivel Esencial.
   */
  const BASE = ['limpiador', 'antioxidante', 'hidratante', 'spf'];

  const BLUEPRINT = {
    basica: ['limpiador', 'antioxidante', 'hidratante', 'spf'],
    esencial: ['limpiador', 'antioxidante', 'tratamiento', 'hidratante', 'spf'],
    completa: ['limpiador', 'antioxidante', 'tratamiento', 'contorno', 'hidratante', 'spf'],
    premium: ['limpiador', 'antioxidante', 'tratamiento', 'contorno', 'hidratante', 'spf', 'mascarilla', 'suplemento'],
  };

  const STEPS = {
    limpiador:   { label: 'Limpiador',            am: 1, pm: 1, freq: 'Mañana y noche' },
    antioxidante:{ label: 'Suero antioxidante',   am: 2,         freq: 'Cada mañana' },
    tratamiento: { label: 'Tratamiento estrella',        pm: 2,  freq: 'Cada noche', hero: true },
    contorno:    { label: 'Contorno de ojos',     am: 3, pm: 3,  freq: 'Mañana y noche' },
    hidratante:  { label: 'Hidratante',           am: 4, pm: 4,  freq: 'Mañana y noche' },
    spf:         { label: 'Protector solar',      am: 5,         freq: 'Cada mañana, todo el año' },
    mascarilla:  { label: 'Mascarilla / exfoliante',      pm: 5,  freq: '1–2 veces por semana' },
    suplemento:  { label: 'Suplemento',           am: 6,         freq: '1 toma al día' },
  };

  /* Activos NO recomendados en embarazo o lactancia. */
  const EMBARAZO_EXCLUIR = [
    'intelligent-retinol',
    'sensilis-pure-age-perfection-retinal',
    'bakuchiol-peptides',
    'crystal-retinal-ceramide-eye-3',
    'svr-serum-a',
    'cyspera-original',
    'cyspera-original-boost',
    'cyspera-intensive-kit',
    'zo-skin-health-pigment-control-creame',
  ];

  /* ======================================================================
     3. REGLAS DE RECOMENDACIÓN  ← edita aquí para cambiar productos
     ====================================================================== */

  const R = (when, esencial, completa, premium, why, basica) =>
    ({ when, esencial, completa, premium, why, basica: basica || esencial });
  const is = (k, ...vals) => (p) => vals.indexOf(p[k]) !== -1;
  const any = (...fns) => (p) => fns.some((f) => f(p));
  const always = () => true;

  const RULES = {
    limpiador: [
      R(any(is('objetivo', 'acne', 'poros'), is('piel', 'grasa')),
        ['gel-moussant', 'acne-cleanser', 'sensilis-purify-essential-cleanser'],
        ['clarifying-foam', 'sensilis-purify-essential-cleanser', 'gel-moussant'],
        ['blemish-age-cleanser-gel', 'clarifying-foam', 'sensilis-purify-essential-cleanser'],
        'Desincrusta el poro y controla la grasa sin resecar. Si el limpiador es agresivo, tu piel produce más grasa de rebote: aquí evitamos justo eso.',
        ['gel-moussant', 'acne-cleanser', 'gentle-cleanser']),

      R(any(is('objetivo', 'rojeces'), is('piel', 'sensible')),
        ['svr-sensifine-dermo-nettoyant', 'gentle-cleanser', 'calmwise-soothing-cleanser'],
        ['calmwise-soothing-cleanser', 'svr-sensifine-dermo-nettoyant', 'gentle-cleanser'],
        ['soothing-cleanser', 'calmwise-soothing-cleanser', 'svr-sensifine-dermo-nettoyant'],
        'Limpia sin detergentes agresivos. Tu barrera está reaccionando, y el primer paso para bajar la rojez es dejar de irritarla dos veces al día.',
        ['gentle-cleanser', 'svr-sensifine-dermo-nettoyant', 'calmwise-soothing-cleanser']),

      R(any(is('objetivo', 'deshidratacion'), is('piel', 'seca')),
        ['sensilis-succinic-jelly-oil-cleanser', 'gentle-cleanser', 'micellar-mousse'],
        ['micellar-mousse', 'sensilis-succinic-jelly-oil-cleanser', 'gentle-cleanser'],
        ['soothing-cleanser', 'micellar-mousse', 'sensilis-succinic-jelly-oil-cleanser'],
        'Textura en aceite que disuelve maquillaje y protector solar, y deja la piel cómoda —nunca esa sensación de tirantez que confundimos con "limpio".',
        ['gentle-cleanser', 'sensilis-succinic-jelly-oil-cleanser', 'micellar-mousse']),

      R(always,
        ['gentle-cleanser', 'sensilis-purify-essential-cleanser', 'micellar-mousse'],
        ['sensilis-purify-essential-cleanser', 'micellar-mousse', 'clarifying-foam'],
        ['micellar-mousse', 'soothing-cleanser', 'clarifying-foam'],
        'El paso que decide si el resto de tu rutina funciona: sin una piel bien limpia, ningún activo penetra.',
        ['gentle-cleanser', 'sensilis-purify-essential-cleanser', 'gel-moussant']),
    ],

    antioxidante: [
      R(is('objetivo', 'manchas'),
        ['sensilis-skin-glow-beyond-c-serum', 'svr-serum-c', 'c-tetra-vitamin-c'],
        ['c-tetra-vitamin-c', 'sensilis-skin-glow-beyond-c-serum', 'svr-serum-c'],
        ['phlorentin-cf', 'ce-ferulic', 'c-tetra-vitamin-c'],
        'Vitamina C cada mañana: frena la enzima que fabrica melanina. Sin esto, aclaras la mancha de noche y la vuelves a formar de día.',
        ['sensilis-skin-glow-beyond-c-serum', 'svr-serum-c', 'c-tetra-vitamin-c']),

      R(any(is('objetivo', 'acne', 'poros'), is('piel', 'grasa')),
        ['svr-serum-c', 'sensilis-skin-glow-beyond-c-serum', 'c-tetra-vitamin-c'],
        ['blemish-age-defense', 'c-tetra-vitamin-c', 'svr-serum-c'],
        ['silymarin-cf', 'blemish-age-defense', 'c-tetra-vitamin-c'],
        'Antioxidante en textura ligera, no comedogénica: baja la inflamación del brote y aclara la marca oscura que deja al sanar.',
        ['svr-serum-c', 'sensilis-skin-glow-beyond-c-serum', 'c-tetra-vitamin-c']),

      R(any(is('objetivo', 'rojeces'), is('piel', 'sensible')),
        ['sensilis-skin-rescue-serum-sos', 'sensilis-pure-age-perfection-cicakn-serum', 'phyto-corrective'],
        ['phyto-corrective', 'sensilis-pure-age-perfection-cicakn-serum', 'sensilis-skin-rescue-serum-sos'],
        ['phyto-corrective', 'resveratrol-be', 'sensilis-skin-rescue-serum-sos'],
        'Calma la rojez visible y refuerza la barrera. Es el antioxidante que tu piel sí tolera, formulado para pieles que reaccionan a todo.',
        ['sensilis-skin-glow-beyond-c-serum', 'sensilis-skin-rescue-serum-sos', 'svr-serum-c']),

      R(is('objetivo', 'deshidratacion'),
        ['hydra-hyal-serum', 'sensilis-skin-glow-beyond-c-serum', 'hydrating-b5'],
        ['hydrating-b5', 'hydra-hyal-serum', 'hab5-hydra-serum'],
        ['h-a-intensifier', 'hab5-hydra-serum', 'hydrating-b5'],
        'Ácido hialurónico de varios pesos moleculares: hidrata en superficie y en profundidad. La luz vuelve desde la primera semana.',
        ['sensilis-skin-glow-beyond-c-serum', 'svr-serum-c', 'hydra-hyal-serum']),

      R(always,
        ['sensilis-skin-glow-beyond-c-serum', 'svr-serum-c', 'c-tetra-vitamin-c'],
        ['c-tetra-vitamin-c', 'svr-serum-c', 'sensilis-skin-glow-beyond-c-serum'],
        ['ce-ferulic', 'resveratrol-be', 'c-tetra-vitamin-c'],
        'Tu escudo de la mañana: neutraliza el daño de sol y contaminación antes de que se convierta en mancha o arruga. Es prevención pura.',
        ['sensilis-skin-glow-beyond-c-serum', 'svr-serum-c', 'c-tetra-vitamin-c']),
    ],

    tratamiento: [
      /* Principiante en antiedad/textura → concentración suave para no irritar */
      R((p) => p.experiencia === 'principiante' && (p.objetivo === 'arrugas' || p.objetivo === 'poros'),
        ['sensilis-pure-age-perfection-retinal', 'bakuchiol-peptides', 'liquid-peptides'],
        ['sensilis-pure-age-perfection-retinal', 'bakuchiol-peptides', 'liquid-peptides'],
        ['bakuchiol-peptides', 'liquid-peptides', 'intelligent-retinol'],
        'Empezamos con una concentración suave para que tu piel se acostumbre sin descamarse. Subimos de nivel en 8 semanas, no antes.'),

      R(is('objetivo', 'acne'),
        ['svr-sebiaclear-ampoule-flash', 'svr-sebiaclear-active-gel', 'clarity-peptides'],
        ['clarity-peptides', 'svr-sebiaclear-ampoule-flash', 'svr-sebiaclear-active-gel'],
        ['clarity-peptides', 'intelligent-retinol', 'svr-sebiaclear-ampoule-flash'],
        'Ataca el brote de hoy y la marca del brote pasado al mismo tiempo, sin el efecto rebote de los tratamientos secantes de farmacia.'),

      R(is('objetivo', 'manchas'),
        ['sensilis-skin-glow-glycolic-gel-alisador', 'zo-skin-health-pigment-control-creame', 'alastin-a-luminate-brightening-serum'],
        ['discoloration-defense', 'alastin-a-luminate-brightening-serum', 'zo-skin-health-pigment-control-creame'],
        ['cyspera-original', 'discoloration-defense', 'alastin-a-luminate-brightening-serum'],
        'Interrumpe la melanina en varios puntos a la vez, que es la única forma de que una mancha no regrese al mes siguiente.'),

      R(is('objetivo', 'arrugas'),
        ['sensilis-pure-age-perfection-retinal', 'liquid-peptides', 'bakuchiol-peptides'],
        ['intelligent-retinol', 'liquid-peptides', 'ncef-revitalize-serum'],
        ['p-tiox', 'intelligent-retinol', 're-time-serum'],
        'Retinoide + péptidos: el único dúo con evidencia clínica real para líneas y firmeza. Trabaja mientras duermes, que es cuando tu piel se repara.'),

      R(is('objetivo', 'rojeces'),
        ['sensilis-skin-rescue-serum-sos', 'neocica', 'sensilis-pure-age-perfection-cicakn-serum'],
        ['sensilis-pure-age-perfection-cicakn-serum', 'phyto-corrective', 'neocica'],
        ['phyto-corrective', 'b3-recovery-cream', 'sensilis-pure-age-perfection-cicakn-serum'],
        'Repara la barrera que deja pasar los irritantes. Cuando la barrera cierra, la rojez baja sola —sin cortisona, sin rebote.'),

      R(is('objetivo', 'deshidratacion'),
        ['hydra-hyal-serum', 'sensilis-pure-age-perfection-cicakn-serum', 'hydrating-b5'],
        ['hydrating-b5', 'hydra-hyal-serum', 'hab5-hydra-serum'],
        ['h-a-intensifier', 'hab5-hydra-serum', 'revision-dej-daily-boosting-serum'],
        'Rellena el agua que tu piel perdió y sella para que no se escape de noche. Es lo que devuelve el "glow" que el maquillaje solo simula.'),

      R(is('objetivo', 'poros'),
        ['mat-pores', 'sensilis-skin-glow-glycolic-gel-alisador', 'clarity-peptides'],
        ['sensilis-skin-glow-glycolic-gel-alisador', 'clarity-peptides', 'skinceuticals-retexturing-activator'],
        ['skinceuticals-retexturing-activator', 'p-tiox', 'clarity-peptides'],
        'Renueva la capa superficial y afina la textura noche a noche. El poro no se cierra, pero deja de verse: se descongestiona y refina.'),

      R(always,
        ['liquid-peptides', 'sensilis-pure-age-perfection-cicakn-serum', 'hydrating-b5'],
        ['liquid-peptides', 'hydrating-b5', 'ncef-revitalize-serum'],
        ['ncef-revitalize-serum', 'liquid-peptides', 'h-a-intensifier'],
        'El activo que hace el trabajo pesado mientras duermes. Es el paso que más cambia tu piel a 90 días.'),
    ],

    hidratante: [
      R(any(is('objetivo', 'rojeces'), is('piel', 'sensible')),
        ['svr-sensifine-ar', 'sensilis-skin-rescue-barrier-crema-reparadora', 'neocica'],
        ['neocica', 'sensilis-skin-rescue-barrier-crema-reparadora', 'svr-sensifine-ar'],
        ['b3-recovery-cream', 'triple-lipid-restore-2-4-2', 'neocica'],
        'Reconstruye la barrera con ceramidas y niacinamida. Es el escudo que evita que mañana vuelvas a reaccionar.',
        ['svr-sensifine-ar', 'sensilis-skin-rescue-barrier-crema-reparadora', 'hydrate-lobelia']),

      R(any(is('objetivo', 'acne', 'poros'), is('piel', 'grasa')),
        ['sensilis-hydra-essence-gel-sorbete', 'svr-sebiaclear-hydra', 'mat-pores'],
        ['svr-sebiaclear-hydra', 'sensilis-hydra-essence-gel-sorbete', 'hydra-hyal-cream'],
        ['hydra-hyal-cream', 'svr-sebiaclear-hydra', 'sensilis-hydra-essence-gel-sorbete'],
        'Gel-crema oil-free: hidrata sin taponar. Saltarte la hidratación es lo que hace que tu piel produzca todavía más grasa.',
        ['sensilis-hydra-essence-gel-sorbete', 'svr-sebiaclear-hydra', 'mat-pores']),

      R((p) => p.objetivo === 'arrugas' || p.edad === '45+',
        ['sensilis-peptide-ar-crema-sorbete-lifting', 'extreme-care', 'hydrate-lobelia'],
        ['ncef-revitalize-cream', 'filorga-global-repair-advanced', 'sensilis-peptide-ar-crema-sorbete-lifting'],
        ['age-interrupter-advanced', 'hp-youth-cream', 'filorga-global-repair-advanced'],
        'Nutre y densifica: repone los lípidos que la piel deja de fabricar con los años. Es lo que devuelve el aspecto "relleno" y firme.',
        ['hydrate-lobelia', 'extreme-care', 'sensilis-peptide-ar-crema-sorbete-lifting']),

      R(any(is('objetivo', 'deshidratacion'), is('piel', 'seca')),
        ['sensilis-hydra-essence-crema-fondant', 'hydrate-lobelia', 'daily-moisture-facial-cream'],
        ['daily-moisture-facial-cream', 'hydrate-lobelia', 'sensilis-hydra-essence-crema-fondant'],
        ['triple-lipid-restore-2-4-2', 'filorga-global-repair-advanced', 'daily-moisture-facial-cream'],
        'Sella toda la hidratación que pusiste antes. Sin este paso, el suero se evapora y te quedas igual de tirante a las dos horas.',
        ['sensilis-hydra-essence-crema-fondant', 'hydrate-lobelia', 'extreme-care']),

      R(is('objetivo', 'manchas'),
        ['sensilis-skin-glow-pearl', 'sensilis-skin-glow-juicy-cream', 'extreme-care'],
        ['sensilis-skin-glow-juicy-cream', 'daily-moisture-facial-cream', 'sensilis-skin-glow-pearl'],
        ['hp-youth-cream', 'skinceuticals-advanced-rgn-6', 'daily-moisture-facial-cream'],
        'Hidrata y unifica el tono al mismo tiempo, para que el resultado del despigmentante se note antes.',
        ['sensilis-skin-glow-pearl', 'sensilis-skin-glow-juicy-cream', 'hydrate-lobelia']),

      R(always,
        ['sensilis-hydra-essence-crema-fondant', 'extreme-care', 'hydrate-lobelia'],
        ['daily-moisture-facial-cream', 'sensilis-skin-glow-juicy-cream', 'extreme-care'],
        ['triple-lipid-restore-2-4-2', 'hp-youth-cream', 'daily-moisture-facial-cream'],
        'La crema que sella tu rutina y mantiene el resultado 24 horas. Sin ella, todo lo anterior se evapora.',
        ['sensilis-hydra-essence-crema-fondant', 'hydrate-lobelia', 'sensilis-skin-glow-juicy-cream']),
    ],

    contorno: [
      R((p) => p.objetivo === 'manchas' || p.objetivo === 'deshidratacion',
        ['sensilis-skin-glow-k-eyes', 'optim-eyes', 'total-eye-2-distintas-tonalidades'],
        ['total-eye-2-distintas-tonalidades', 'optim-eyes', 'alastin-restorative-eye-complex'],
        ['zo-skin-health-eye-brigthening-creme', 'age-advanced-eye', 'total-eye-2-distintas-tonalidades'],
        'La ojera oscura y la bolsa son de origen vascular: este trabaja el drenaje y la microcirculación, no solo hidrata.'),

      R((p) => p.objetivo === 'arrugas' || p.edad === '45+' || p.edad === '35-44',
        ['sensilis-skin-glow-k-eyes', 'optim-eyes', 'alastin-restorative-eye-complex'],
        ['crystal-retinal-ceramide-eye-3', 'alastin-restorative-eye-complex', 'time-filler-eyes-5xp'],
        ['age-advanced-eye', 'time-filler-eyes-5xp', 'alastin-restorative-eye-complex'],
        'La piel del contorno es 4 veces más delgada: ahí aparece la primera arruga y ahí se nota primero el cansancio.'),

      R(always,
        ['sensilis-skin-glow-k-eyes', 'optim-eyes', 'alastin-restorative-eye-complex'],
        ['optim-eyes', 'alastin-restorative-eye-complex', 'sensilis-skin-glow-k-eyes'],
        ['time-filler-eyes-5xp', 'alastin-restorative-eye-complex', 'optim-eyes'],
        'Prevención pura: el contorno se marca años antes que el resto de la cara. Empezar hoy cuesta mucho menos que corregir después.'),
    ],

    spf: [
      R(is('objetivo', 'manchas'),
        ['colorescience-tint-du-soleil', 'colorescience-no-show-mineral-sunscreen-spf-50', 'ecran'],
        ['colorescience-tint-du-soleil', 'colorescience-flex', 'colorescience-no-show-mineral-sunscreen-spf-50'],
        ['colorescience-flex', 'colorescience-tint-du-soleil', 'colorescience-no-show-mineral-sunscreen-spf-50'],
        'Filtro mineral CON color: bloquea también la luz visible del sol y de las pantallas, que es la que dispara el melasma. Un SPF transparente no la detiene.',
        ['svr-sun-secure-blur', 'ecran', 'brume']),

      R(any(is('objetivo', 'acne', 'poros'), is('piel', 'grasa', 'mixta')),
        ['colorescience-no-show-mineral-sunscreen-spf-50', 'tizo-prime-sunscreen', 'svr-sun-secure-blur'],
        ['colorescience-no-show-mineral-sunscreen-spf-50', 'colorescience-flex', 'tizo-prime-sunscreen'],
        ['colorescience-no-show-mineral-sunscreen-spf-50', 'colorescience-flex', 'colorescience-tint-du-soleil'],
        'Acabado seco y matificante, sin residuo graso ni blanco. Es el protector que sí te vas a poner todos los días, y ese es el que funciona.',
        ['svr-sun-secure-blur', 'ecran', 'brume']),

      R(any(is('objetivo', 'rojeces'), is('piel', 'sensible')),
        ['colorescience-no-show-mineral-sunscreen-spf-50', 'tizo-prime-sunscreen', 'ecran'],
        ['colorescience-no-show-mineral-sunscreen-spf-50', 'colorescience-tint-du-soleil', 'tizo-prime-sunscreen'],
        ['colorescience-no-show-mineral-sunscreen-spf-50', 'colorescience-flex', 'colorescience-tint-du-soleil'],
        'Filtro 100% mineral: se queda sobre la piel en lugar de absorberse, así que no genera calor ni reacción en pieles reactivas.',
        ['ecran', 'brume', 'svr-sun-secure-blur']),

      R(always,
        ['colorescience-no-show-mineral-sunscreen-spf-50', 'tizo-prime-sunscreen', 'svr-sun-secure-blur'],
        ['colorescience-flex', 'colorescience-no-show-mineral-sunscreen-spf-50', 'colorescience-tint-du-soleil'],
        ['colorescience-flex', 'colorescience-tint-du-soleil', 'colorescience-no-show-mineral-sunscreen-spf-50'],
        'El 80% del envejecimiento visible viene del sol. Este es el producto que más resultado te va a dar por peso invertido, sin discusión.',
        ['svr-sun-secure-blur', 'ecran', 'tizo-prime-sunscreen']),
    ],

    mascarilla: [
      R(any(is('objetivo', 'acne', 'poros'), is('piel', 'grasa')),
        ['sensilis-prep4-polish-mousse-exfoliante', 'mascarilla-exfoliante', 'zo-skin-health-oil-control-pads'],
        ['zo-skin-health-oil-control-pads', 'sensilis-prep4-polish-mousse-exfoliante', 'glycopeel-mask'],
        ['glycopeel-mask', 'zo-skin-health-oil-control-pads', 'sensilis-prep4-polish-mousse-exfoliante'],
        'Dos veces por semana: descongestiona el poro y evita que el sebo acumulado se convierta en el brote del mes que viene.'),

      R(is('objetivo', 'arrugas', 'manchas'),
        ['time-filler-mask', 'mascarilla-exfoliante', 'sensilis-prep4-polish-mousse-exfoliante'],
        ['time-filler-mask', 'sensilis-prep4-polish-mousse-exfoliante', 'gr-youth-mask'],
        ['gr-youth-mask', 'glycopeel-mask', 'time-filler-mask'],
        'El empujón semanal: renueva la superficie para que la piel refleje mejor la luz y los activos penetren el doble.'),

      R(always,
        ['mascarilla-exfoliante', 'time-filler-mask', 'sensilis-prep4-polish-mousse-exfoliante'],
        ['time-filler-mask', 'sensilis-prep4-polish-mousse-exfoliante', 'gr-youth-mask'],
        ['gr-youth-mask', 'time-filler-mask', 'glycopeel-mask'],
        'Tu "facial en casa" semanal: 10 minutos que multiplican el resultado de todo lo demás.'),
    ],

    suplemento: [
      R(is('objetivo', 'manchas'),
        ['glisodin-advanced-skin-brightening-formula', 'neovitamins-omega-3'],
        ['glisodin-advanced-skin-brightening-formula', 'neovitamins-omega-3'],
        ['glisodin-advanced-skin-brightening-formula', 'neovitamins-omega-3'],
        'La mancha también se combate desde adentro: antioxidantes orales que reducen la respuesta pigmentaria al sol.'),

      R(is('objetivo', 'arrugas'),
        ['glisodin-advanced-anti-aging-formula', 'neovitamins-telomere-elite'],
        ['glisodin-advanced-anti-aging-formula', 'neovitamins-telomere-elite'],
        ['glisodin-advanced-anti-aging-formula', 'neovitamins-telomere-elite'],
        'Nutricosmética: trabaja el colágeno desde dentro, donde ninguna crema llega.'),

      R(is('objetivo', 'acne'),
        ['neovitamins-myo-d-chiro-inositol', 'neovitamins-omega-3'],
        ['neovitamins-myo-d-chiro-inositol', 'neovitamins-omega-3'],
        ['neovitamins-myo-d-chiro-inositol', 'neovitamins-omega-3'],
        'Cuando el brote es hormonal, lo tópico solo alcanza hasta cierto punto. Esto trabaja la raíz del problema.'),

      R(always,
        ['neovitamins-omega-3', 'glisodin-advanced-anti-aging-formula'],
        ['neovitamins-omega-3', 'glisodin-advanced-anti-aging-formula'],
        ['neovitamins-omega-3', 'glisodin-advanced-anti-aging-formula'],
        'Omega 3 refuerza la barrera lipídica desde el interior: menos inflamación, menos deshidratación.'),
    ],
  };

  /**
   * Condición de cada regla en español, EN EL MISMO ORDEN que arriba.
   * Solo sirve para la hoja de revisión clínica: si agregas o quitas una regla,
   * agrega o quita también su línea aquí.
   */
  const RULE_LABELS = {
    limpiador: [
      'Objetivo acné o poros · o piel grasa',
      'Objetivo rojeces · o piel sensible',
      'Objetivo piel apagada · o piel seca',
      'Cualquier otro caso (piel normal o mixta)',
    ],
    antioxidante: [
      'Objetivo manchas',
      'Objetivo acné o poros · o piel grasa',
      'Objetivo rojeces · o piel sensible',
      'Objetivo piel apagada',
      'Cualquier otro caso (líneas, piel normal)',
    ],
    tratamiento: [
      'No usa nada o solo limpiador y crema · y busca líneas o textura',
      'Objetivo acné',
      'Objetivo manchas',
      'Objetivo líneas y flacidez',
      'Objetivo rojeces',
      'Objetivo piel apagada',
      'Objetivo grasa y poros',
      'Cualquier otro caso',
    ],
    hidratante: [
      'Objetivo rojeces · o piel sensible',
      'Objetivo acné o poros · o piel grasa',
      'Objetivo líneas · o 45 años o más',
      'Objetivo piel apagada · o piel seca',
      'Objetivo manchas',
      'Cualquier otro caso',
    ],
    contorno: [
      'Objetivo manchas o piel apagada (ojera oscura)',
      'Objetivo líneas · o 35 años o más',
      'Cualquier otro caso',
    ],
    spf: [
      'Objetivo manchas',
      'Objetivo acné o poros · o piel grasa o mixta',
      'Objetivo rojeces · o piel sensible',
      'Cualquier otro caso',
    ],
    mascarilla: [
      'Objetivo acné o poros · o piel grasa',
      'Objetivo líneas o manchas',
      'Cualquier otro caso',
    ],
    suplemento: [
      'Objetivo manchas',
      'Objetivo líneas y flacidez',
      'Objetivo acné',
      'Cualquier otro caso',
    ],
  };

  /* Red de seguridad: si el embarazo excluye todo lo demás. */
  const SAFE_FALLBACK = {
    tratamiento: ['liquid-peptides', 'hydrating-b5', 'phyto-corrective'],
    contorno: ['optim-eyes', 'sensilis-skin-glow-k-eyes'],
    mascarilla: ['time-filler-mask', 'mascarilla-exfoliante'],
  };

  /**
   * Cuántos días rinde cada producto — base del "cuánto me cuesta al día".
   * Sale de product_duration_data.json; los que no tenían dato se estimaron
   * por tipo de producto, siempre por lo bajo para no prometer de más.
   */
  const DIAS_DEFAULT = { Limpiador: 100, Suero: 60, Crema: 75, 'Protector Solar': 60,
    'Contorno de Ojos': 90, Mascarilla: 100, Exfoliante: 100, Pads: 100, Suplemento: 30 };

  const DIAS = {
    'acne-cleanser': 105, 'age-advanced-eye': 90, 'age-interrupter-advanced': 75,
    'alastin-a-luminate-brightening-serum': 75, 'alastin-restorative-eye-complex': 90,
    'b3-recovery-cream': 75, 'bakuchiol-peptides': 75, 'blemish-age-cleanser-gel': 105,
    'blemish-age-defense': 60, 'brume': 60, 'c-tetra-vitamin-c': 60, 'calmwise-soothing-cleanser': 105,
    'ce-ferulic': 60, 'clarifying-foam': 105, 'clarity-peptides': 60, 'colorescience-flex': 60,
    'colorescience-no-show-mineral-sunscreen-spf-50': 60, 'colorescience-tint-du-soleil': 90,
    'crystal-retinal-ceramide-eye-3': 90, 'cyspera-intensive-kit': 75, 'cyspera-original': 75,
    'cyspera-original-boost': 75, 'daily-moisture-facial-cream': 75, 'discoloration-defense': 60,
    'ecran': 60, 'extreme-care': 75, 'filorga-global-repair-advanced': 75, 'gel-moussant': 105,
    'gentle-cleanser': 105, 'glisodin-advanced-anti-aging-formula': 30,
    'glisodin-advanced-skin-brightening-formula': 30, 'glycopeel-mask': 135, 'gr-youth-mask': 135,
    'h-a-intensifier': 60, 'hab5-hydra-serum': 60, 'hp-youth-cream': 75, 'hydra-hyal-cream': 75,
    'hydra-hyal-serum': 60, 'hydrate-lobelia': 75, 'hydrating-b5': 60, 'intelligent-retinol': 90,
    'liquid-peptides': 60, 'mascarilla-exfoliante': 135, 'mat-pores': 75, 'micellar-mousse': 105,
    'ncef-revitalize-cream': 75, 'ncef-revitalize-serum': 60, 'neocica': 60,
    'neovitamins-myo-d-chiro-inositol': 30, 'neovitamins-omega-3': 30, 'neovitamins-telomere-elite': 30,
    'optim-eyes': 90, 'p-tiox': 60, 'phlorentin-cf': 60, 'phyto-corrective': 60, 're-time-serum': 60,
    'resveratrol-be': 60, 'revision-dej-daily-boosting-serum': 60,
    'sensilis-hydra-essence-crema-fondant': 75, 'sensilis-hydra-essence-gel-sorbete': 75,
    'sensilis-peptide-ar-crema-sorbete-lifting': 75, 'sensilis-prep4-polish-mousse-exfoliante': 100,
    'sensilis-pure-age-perfection-cicakn-serum': 60, 'sensilis-pure-age-perfection-retinal': 75,
    'sensilis-purify-essential-cleanser': 100, 'sensilis-skin-glow-beyond-c-serum': 60,
    'sensilis-skin-glow-glycolic-gel-alisador': 75, 'sensilis-skin-glow-juicy-cream': 75,
    'sensilis-skin-glow-k-eyes': 90, 'sensilis-skin-glow-pearl': 75,
    'sensilis-skin-rescue-barrier-crema-reparadora': 75, 'sensilis-skin-rescue-serum-sos': 60,
    'sensilis-succinic-jelly-oil-cleanser': 100, 'silymarin-cf': 60, 'skinceuticals-advanced-rgn-6': 75,
    'skinceuticals-retexturing-activator': 60, 'soothing-cleanser': 105, 'svr-sebiaclear-active-gel': 75,
    'svr-sebiaclear-ampoule-flash': 60, 'svr-sebiaclear-hydra': 75, 'svr-sensifine-ar': 75,
    'svr-sensifine-dermo-nettoyant': 100, 'svr-serum-a': 60, 'svr-serum-c': 60, 'svr-sun-secure-blur': 60,
    'time-filler-eyes-5xp': 90, 'time-filler-mask': 135, 'tizo-prime-sunscreen': 75,
    'total-eye-2-distintas-tonalidades': 90, 'triple-lipid-restore-2-4-2': 75,
    'zo-skin-health-eye-brigthening-creme': 90, 'zo-skin-health-oil-control-pads': 100,
    'zo-skin-health-pigment-control-creame': 75,
  };

  /** Días que rinde un producto (dato propio, si no por tipo, si no 75). */
  function diasDe(prod) {
    return DIAS[prod.handle] || DIAS_DEFAULT[prod.type] || 75;
  }

  /** Costo por día de un producto: lo que cuesta entre lo que dura. */
  function porDia(prod) {
    return prod.price / diasDe(prod);
  }

  function mesesTexto(dias) {
    const m = dias / 30;
    if (m < 1.25) return 'cerca de un mes';
    if (m < 1.75) return 'mes y medio';
    return 'unos ' + Math.round(m) + ' meses';
  }

  /* ======================================================================
     4. TEXTOS DEL DIAGNÓSTICO
     ====================================================================== */

  const PERFIL = {
    objetivo: {
      acne: 'Brotes y marcas', manchas: 'Manchas y tono disparejo', arrugas: 'Líneas y firmeza',
      deshidratacion: 'Deshidratación y falta de luz', rojeces: 'Rojeces y sensibilidad', poros: 'Poros y grasa',
    },
    piel: { grasa: 'Piel grasa', mixta: 'Piel mixta', seca: 'Piel seca', normal: 'Piel normal', sensible: 'Piel sensible' },
    experiencia: { principiante: 'Nivel principiante', intermedio: 'Nivel intermedio', avanzado: 'Nivel avanzado' },
    nivel: { basica: 'Rutina básica', esencial: 'Rutina esencial', completa: 'Rutina completa', premium: 'Rutina pro' },
  };

  const DIAGNOSTICO = {
    acne: 'Tu piel está produciendo más sebo del que puede drenar, y ese tapón es el que termina en brote. La trampa más común es atacarlo con productos secantes: la piel se deshidrata, se defiende produciendo más grasa y el ciclo se repite. Tu rutina hace lo contrario —regula sin resecar, y trata al mismo tiempo la marca que deja cada granito.',
    manchas: 'La mancha tiene dos caras: la que ya está formada y la que se sigue produciendo cada vez que te da el sol. Tratar solo una es la razón número uno por la que la gente no ve resultados. Tu rutina ataca las dos: despigmentante de noche, antioxidante y filtro mineral de día. Sin ese segundo frente, la mancha regresa en semanas.',
    arrugas: 'Las primeras líneas no aparecen por falta de crema, sino porque el colágeno se degrada más rápido de lo que se produce. Tu rutina combina el activo con más evidencia clínica para estimularlo con la nutrición que necesita la piel para reconstruirse de noche. Es un trabajo de meses, pero es el único que sí cambia la estructura.',
    deshidratacion: 'Deshidratada no es lo mismo que seca: te falta agua, no grasa. Por eso puede brillar y sentirse tirante al mismo tiempo. Tu rutina trabaja en dos tiempos: repone el agua con ácido hialurónico y después la sella para que no se evapore. Es el cambio que se nota más rápido de todos —cuestión de días.',
    rojeces: 'Tu barrera cutánea está permeable: deja escapar agua y deja entrar irritantes. Eso es lo que provoca la rojez, el ardor y que "todo te haga reacción". La prioridad no es corregir nada todavía, es reparar. Tu rutina es deliberadamente corta y sin activos agresivos: primero cerramos la barrera, después construimos.',
    poros: 'El poro no se abre ni se cierra: se dilata cuando se llena. Lo que se ve como "poro grande" casi siempre es sebo oxidado y células muertas acumuladas. Tu rutina renueva la superficie de forma constante y controla el sebo, para que el poro se vacíe y refleje la luz de otra forma. La textura cambia antes que el tamaño.',
  };

  /* ======================================================================
     5. UTILIDADES
     ====================================================================== */

  const money = (cents) =>
    window.SkinTalk && SkinTalk.formatMoney
      ? SkinTalk.formatMoney(cents)
      : '$' + (Number(cents || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function imgUrl(url, w) {
    if (!url) return '';
    let u = url.indexOf('//') === 0 ? 'https:' + url : url;
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function encodeAnswers(a) {
    try {
      const flat = ANSWER_KEYS.map((k) => (Array.isArray(a[k]) ? a[k].join('.') : a[k] || '')).join('~');
      return btoa(unescape(encodeURIComponent(flat))).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
    } catch (e) { return ''; }
  }

  /** 10 dígitos (México) o 11–15 con lada de país. La app lo normaliza a E.164. */
  function telefonoValido(t) {
    const d = String(t || '').replace(/\D/g, '');
    return d.length === 10 || (d.length >= 11 && d.length <= 15);
  }

  function decodeAnswers(code) {
    try {
      const flat = decodeURIComponent(escape(atob(code.replace(/-/g, '+').replace(/_/g, '/'))));
      const parts = flat.split('~');
      const a = {};
      ANSWER_KEYS.forEach((k, i) => {
        if (k === 'notas') { a.notas = parts[i] ? parts[i].split('.') : []; return; }
        if (parts[i]) a[k] = parts[i];
      });
      return a.objetivo && a.piel && a.nivel ? a : null;
    } catch (e) { return null; }
  }

  /* ======================================================================
     5.b MEDICIÓN PROPIA
     ----------------------------------------------------------------------
     GA y Meta solo dicen cuánta gente pasó. Para saber EN QUÉ PREGUNTA se cae
     y qué perfil tiene quien no deja su correo hace falta guardar los eventos
     en nuestro propio servidor: eso es lo que alimenta el panel.
     ====================================================================== */

  /** URL de la app que recibe los eventos. La pone el componente al arrancar. */
  let APP_URL = '';

  /**
   * Tienda a la que apuntan las ligas de producto y el carrito. Vacío = este
   * mismo sitio (el quiz corriendo dentro de Shopify). Cuando el quiz vive
   * fuera de la tienda (skintalk-app), es https://skintalk.mx.
   */
  let STORE = '';

  const SESSION_KEY = 'skintalk_quiz_session';

  /** Id de sesión estable por navegador, para poder unir los eventos sueltos.
      Si entra desde la liga de su WhatsApp, se usa la sesión con la que hizo el quiz. */
  let sessionId = (function () {
    const nuevo = function () {
      try {
        if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
      } catch (e) { /* noop */ }
      return 'sq-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    };
    try {
      let id = localStorage.getItem(SESSION_KEY);
      if (!id) { id = nuevo(); localStorage.setItem(SESSION_KEY, id); }
      return id;
    } catch (e) { return nuevo(); }
  })();

  /** De dónde llegó: se manda una vez y sirve para atribuir la campaña. */
  function origen() {
    const out = {};
    try {
      const q = new URLSearchParams(window.location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(function (k) {
        const v = q.get(k);
        if (v) out[k] = v.slice(0, 120);
      });
      if (document.referrer) out.referrer = document.referrer.slice(0, 500);
      out.landing = window.location.pathname.slice(0, 300);
    } catch (e) { /* noop */ }
    return out;
  }

  /**
   * Manda un evento al servidor.
   *
   * `keepalive` para que sobreviva a que la persona cierre la pestaña justo
   * después de contestar. Cualquier error se traga en silencio: medir jamás
   * debe romperle el quiz a la clienta.
   */
  function beacon(body) {
    if (!APP_URL) return Promise.resolve();
    try {
      return fetch(APP_URL.replace(/\/+$/, '') + '/api/quiz', {
        method: 'POST',
        mode: 'cors',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ sessionId: sessionId, meta: origen() }, body)),
      }).catch(function () { /* silencioso */ });
    } catch (e) { return Promise.resolve(); }
  }

  const track = (event, data, extra) => {
    try {
      if (window.SkinTalk && SkinTalk.publish) SkinTalk.publish(event, data);
      if (window.dataLayer) window.dataLayer.push(Object.assign({ event: event }, data || {}));
      if (typeof window.gtag === 'function') window.gtag('event', event.replace(':', '_'), data || {});
      if (typeof window.fbq === 'function' && event === 'quiz:lead') window.fbq('track', 'Lead');
      if (typeof window.fbq === 'function' && event === 'quiz:completed') window.fbq('track', 'CompleteRegistration');
    } catch (e) { /* silencioso */ }
    return beacon(Object.assign({ event: event, data: data || {} }, extra || {}));
  };

  /* ======================================================================
     6. MOTOR
     ====================================================================== */

  const productCache = {};

  async function fetchProduct(handle) {
    if (productCache[handle] !== undefined) return productCache[handle];
    try {
      const res = await fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('404');
      const p = await res.json();
      productCache[handle] = p;
      return p;
    } catch (e) {
      productCache[handle] = null;
      return null;
    }
  }

  /** Devuelve la cadena de handles candidatos para un paso, en orden de preferencia. */
  function candidatesFor(stepKey, profile) {
    const rules = RULES[stepKey] || [];
    const tier = profile.nivel || 'completa';
    const order = tier === 'premium' ? ['premium', 'completa', 'esencial', 'basica']
      : tier === 'esencial' ? ['esencial', 'basica', 'completa', 'premium']
        : tier === 'basica' ? ['basica', 'esencial', 'completa', 'premium']
          : ['completa', 'premium', 'esencial', 'basica'];

    const rule = rules.find((r) => r.when(profile)) || rules[rules.length - 1];
    let chain = [];
    order.forEach((t) => { chain = chain.concat(rule[t] || []); });

    /* Reglas siguientes como red extra (por si todo está agotado) */
    rules.forEach((r) => { if (r !== rule) chain = chain.concat(r[order[0]] || []); });
    chain = chain.concat(SAFE_FALLBACK[stepKey] || []);

    const embarazo = (profile.notas || []).indexOf('embarazo') !== -1;
    if (embarazo) chain = chain.filter((h) => EMBARAZO_EXCLUIR.indexOf(h) === -1);

    /* únicos, preservando orden */
    return chain.filter((h, i) => chain.indexOf(h) === i);
  }

  /** Resuelve la rutina completa contra el catálogo en vivo. */
  async function buildRoutine(answers) {
    const profile = profileOf(answers);
    const keys = BLUEPRINT[profile.nivel] || BLUEPRINT.completa;
    const used = {};

    const resolved = await Promise.all(keys.map(async (key) => {
      const chain = candidatesFor(key, profile);
      const products = await Promise.all(chain.slice(0, 6).map(fetchProduct));
      const options = products.filter((p) => p && p.available && p.variants.some((v) => v.available));
      const rule = (RULES[key] || []).find((r) => r.when(profile)) || (RULES[key] || [])[0];
      return { key: key, options: options, why: rule ? rule.why : '' };
    }));

    /* Evita repetir el mismo producto en dos pasos distintos */
    const steps = [];
    resolved.forEach((r) => {
      const opts = r.options.filter((p) => !used[p.handle]);
      const list = opts.length ? opts : r.options;
      if (!list.length) return;
      used[list[0].handle] = true;
      steps.push({
        key: r.key,
        label: STEPS[r.key].label,
        freq: freqFor(r.key, profile),
        hero: !!STEPS[r.key].hero,
        why: r.why,
        options: list,
        index: 0,
      });
    });
    return steps;
  }

  function freqFor(key, profile) {
    if (key === 'tratamiento' && profile.experiencia === 'principiante') {
      return 'Empieza 2–3 noches por semana';
    }
    return STEPS[key].freq;
  }

  function warnings(answers) {
    const profile = profileOf(answers);
    const out = [];
    if (profile.notas.indexOf('embarazo') !== -1) {
      out.push('Tu rutina está formulada <strong>sin retinoides ni despigmentantes contraindicados</strong> en embarazo y lactancia.');
    }
    if (profile.experiencia === 'principiante') {
      out.push('Introduce <strong>un producto nuevo cada 5 días</strong>. Así, si algo no te cae bien, sabes exactamente qué fue.');
    }
    if (profile.piel === 'sensible' || profile.objetivo === 'rojeces') {
      out.push('Si tienes rosácea diagnosticada o estás en tratamiento dermatológico, <strong>enséñale esta rutina a tu médico</strong> antes de empezar.');
    }
    return out;
  }

  /* ======================================================================
     7. COMPONENTE
     ====================================================================== */

  class SkinQuiz extends Base {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.cfg = JSON.parse(this.querySelector('[data-quiz-config]').textContent);
      APP_URL = this.cfg.app_url || '';
      STORE = (this.cfg.store_url || '').replace(/\/+$/, '');

      /* Por dónde se manda la rutina. WhatsApp lo manda la app, así que sin
         URL de la app el quiz vuelve a pedir correo (con sus textos de correo)
         en vez de juntar números a los que nadie les va a escribir. */
      this.canal = this.cfg.lead_channel === 'email' || !APP_URL ? 'email' : 'whatsapp';
      if (this.canal === 'email' && this.cfg.lead_channel !== 'email') {
        Object.assign(this.cfg, {
          gate_heading: '¿A qué correo te la enviamos?',
          gate_subheading: 'Tu rutina de {n} productos ya está lista. Te la mandamos por correo con tu descuento para que no la pierdas.',
          gate_bullets: ['Tu rutina completa por escrito, con el orden de aplicación', 'Tu código de descuento para estrenarla', 'Seguimiento de la Dra. las primeras 4 semanas'],
          gate_cta: 'Ver mi rutina personalizada',
        });
      }

      this.root = this.querySelector('[data-quiz-root]');
      this.form = this.querySelector('[data-quiz-customer-form] form');
      this.answers = {};
      this.step = -1;
      this.routine = null;
      this.lead = null;
      this.moment = 'am';

      /* Delegación: el DOM se re-renderiza completo en cada paso. */
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      const params = new URLSearchParams(window.location.search);
      const shared = params.get('rutina');
      const saved = this.loadSaved();

      /* La liga de su WhatsApp: ?r=<codigo> abre SU rutina; ?e=<codigo>, la
         misma abierta en lo esencial. El código lo resuelve la app. */
      const codigo = params.get('r') || params.get('e');
      if (codigo && APP_URL && /^[a-zA-Z0-9]{5,12}$/.test(codigo)) {
        this.abrirRutina(codigo, Boolean(params.get('e')));
        return;
      }

      if (shared) {
        const a = decodeAnswers(shared);
        if (a) { this.answers = a; this.lead = saved && saved.lead ? saved.lead : null; this.showResults(true); return; }
      }
      if (params.get('customer_posted') === 'true' && saved && saved.answers) {
        this.answers = saved.answers; this.lead = saved.lead; this.showResults(true); return;
      }
      this.renderIntro(saved);
    }

    /** Su rutina a partir del código de la liga de WhatsApp. Si algo falla, el quiz normal. */
    async abrirRutina(codigo, esencial) {
      this.paint(`<div class="sq-window sq-analysis"><div class="sq-window__bar"><span class="sq-dot"></span><span class="sq-dot"></span><span class="sq-dot"></span><span class="sq-window__name">tu-rutina.folder</span></div><div class="sq-window__body"><div class="sq-analysis__ring"><span></span></div><p class="sq-analysis__msg">Abriendo tu rutina…</p></div></div>`);
      try {
        const res = await fetch(APP_URL.replace(/\/+$/, '') + '/api/rutina/' + codigo, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('rutina ' + res.status);
        const d = await res.json();
        if (!d || !d.answers || !d.answers.objetivo) throw new Error('rutina vacía');
        this.codigo = codigo;
        if (d.sesion) sessionId = d.sesion; // sin guardarla: el navegador conserva la suya
        this.answers = d.answers;
        /* Ya dejó su WhatsApp: no se le vuelve a pedir. */
        this.lead = { nombre: d.nombre || '', desdeWhatsapp: true };
        track('rutina:vista', { codigo: codigo, esencial: Boolean(esencial) });
        await this.showResults(true);
        if (esencial) {
          const el = this.querySelector('.sq-starter');
          if (el) { el.classList.add('is-destacado'); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        }
      } catch (e) {
        this.renderIntro(this.loadSaved());
      }
    }

    /* ---------- persistencia ---------- */
    loadSaved() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const d = JSON.parse(raw);
        return Date.now() - d.ts < 1000 * 60 * 60 * 24 * 30 ? d : null;
      } catch (e) { return null; }
    }

    save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          answers: this.answers, lead: this.lead, ts: Date.now(),
        }));
      } catch (e) { /* modo privado */ }
    }

    /* ---------- navegación ---------- */
    paint(html) {
      this.root.innerHTML = html;
      /* Solo re-encuadra si el quiz quedó fuera de vista por arriba. */
      const rect = this.getBoundingClientRect();
      if (rect.top < -40) {
        window.scrollTo({ top: rect.top + window.scrollY - 90, behavior: 'smooth' });
      }
    }

    renderIntro(saved) {
      const c = this.cfg;
      const resume = saved && saved.answers && saved.answers.objetivo
        ? `<button class="sq-link" data-action="resume">Ya hice el diagnóstico — ver mi rutina</button>` : '';

      this.paint(`
        <div class="sq-window sq-intro">
          <div class="sq-window__bar"><span class="sq-dot"></span><span class="sq-dot"></span><span class="sq-dot"></span>
            <span class="sq-window__name">diagnóstico-de-piel</span></div>
          <div class="sq-window__body">
            <div class="sq-intro__grid">
              <div class="sq-intro__copy">
                <span class="sq-eyebrow">${esc(c.eyebrow)}</span>
                <h2 class="sq-intro__title">${c.heading}</h2>
                <p class="sq-intro__lead">${esc(c.subheading)}</p>
                <ul class="sq-checks">
                  ${c.bullets.map((b) => `<li>${esc(b)}</li>`).join('')}
                </ul>
                <button class="sq-btn sq-btn--primary" data-action="start">${esc(c.cta_label)}</button>
                ${resume}
                <p class="sq-microcopy">${esc(c.intro_note)}</p>
              </div>
              ${c.intro_image ? `<div class="sq-intro__media"><img src="${esc(c.intro_image)}" alt="" loading="lazy"></div>` : ''}
            </div>
          </div>
        </div>
      `);
    }

    renderQuestion() {
      const q = QUESTIONS[this.step];
      const pct = Math.round((this.step / QUESTIONS.length) * 100);
      const current = this.answers[q.id];
      const selected = (v) => q.multi ? (current || []).indexOf(v) !== -1 : current === v;

      this.paint(`
        <div class="sq-window">
          <div class="sq-window__bar"><span class="sq-dot"></span><span class="sq-dot"></span><span class="sq-dot"></span>
            <span class="sq-window__name">pregunta-${this.step + 1}-de-${QUESTIONS.length}</span></div>
          <div class="sq-progress"><div class="sq-progress__fill" style="width:${pct}%"></div></div>
          <div class="sq-window__body sq-question">
            <div class="sq-question__head">
              ${this.step > 0 ? `<button class="sq-back" data-action="back" aria-label="Anterior">←</button>` : '<span></span>'}
              <span class="sq-eyebrow">${esc(q.eyebrow)} · ${this.step + 1}/${QUESTIONS.length}</span>
            </div>
            <h3 class="sq-question__title">${esc(q.title)}</h3>
            <p class="sq-question__help">${esc(q.help)}</p>
            <div class="sq-options${q.cols === 2 ? ' sq-options--2' : ''}" role="${q.multi ? 'group' : 'radiogroup'}">
              ${q.options.map((o) => `
                <button type="button"
                  class="sq-option${selected(o.v) ? ' is-selected' : ''}${o.featured ? ' is-featured' : ''}"
                  data-value="${esc(o.v)}" role="${q.multi ? 'checkbox' : 'radio'}" aria-checked="${selected(o.v)}">
                  ${o.emoji ? `<span class="sq-option__emoji" aria-hidden="true">${o.emoji}</span>` : ''}
                  <span class="sq-option__text">
                    <span class="sq-option__label">${esc(o.label)}</span>
                    ${o.desc ? `<span class="sq-option__desc">${esc(o.desc)}</span>` : ''}
                  </span>
                  ${o.tag ? `<span class="sq-option__tag">${esc(o.tag)}</span>` : ''}
                  <span class="sq-option__check" aria-hidden="true"></span>
                </button>`).join('')}
            </div>
            ${q.check ? `
              <button type="button" class="sq-check${(this.answers.notas || []).indexOf(q.check.v) !== -1 ? ' is-on' : ''}"
                data-action="toggle-note" data-note="${esc(q.check.v)}" role="checkbox"
                aria-checked="${(this.answers.notas || []).indexOf(q.check.v) !== -1}">
                <span class="sq-check__box" aria-hidden="true"></span>
                <span class="sq-check__text">
                  <span class="sq-check__label">${esc(q.check.label)}</span>
                  <span class="sq-check__desc">${esc(q.check.desc)}</span>
                </span>
              </button>` : ''}
            ${q.footnote ? `<p class="sq-footnote">${esc(q.footnote)}</p>` : ''}
            ${q.multi || q.manual ? `<button class="sq-btn sq-btn--primary sq-btn--wide" data-action="next"${q.manual && !current ? ' disabled' : ''}>${esc(q.cta || 'Continuar')}</button>` : ''}
          </div>
        </div>
      `);
    }

    answer(value) {
      const q = QUESTIONS[this.step];
      if (q.multi) {
        let list = (this.answers[q.id] || []).slice();
        const opt = q.options.find((o) => o.v === value);
        if (opt && opt.exclusive) list = list.indexOf(value) !== -1 ? [] : [value];
        else {
          list = list.filter((v) => {
            const o = q.options.find((x) => x.v === v);
            return !(o && o.exclusive);
          });
          list = list.indexOf(value) !== -1 ? list.filter((v) => v !== value) : list.concat([value]);
        }
        this.answers[q.id] = list;
        this.renderQuestion();
        return;
      }
      this.answers[q.id] = value;
      track('quiz:answer', { question: q.id, value: value, step: this.step + 1 },
        { answers: this.answers, step: this.step + 1 });
      this.querySelectorAll('.sq-option').forEach((b) => {
        const on = b.dataset.value === value;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      /* La última pregunta no avanza sola: se decide con el botón. */
      if (q.manual) {
        const cta = this.querySelector('[data-action="next"]');
        if (cta) cta.disabled = false;
        return;
      }
      setTimeout(() => this.next(), 220);
    }

    toggleNote(note) {
      const list = (this.answers.notas || []).slice();
      const i = list.indexOf(note);
      if (i === -1) list.push(note); else list.splice(i, 1);
      this.answers.notas = list;
      const btn = this.querySelector(`[data-note="${note}"]`);
      if (btn) {
        const on = list.indexOf(note) !== -1;
        btn.classList.toggle('is-on', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
      }
    }

    next() {
      const q = QUESTIONS[this.step];
      if (q && q.manual && !this.answers[q.id]) return;
      this.step += 1;
      this.save();
      if (this.step >= QUESTIONS.length) this.runAnalysis();
      else this.renderQuestion();
    }

    /* ---------- análisis ---------- */
    async runAnalysis() {
      track('quiz:completed', { objetivo: this.answers.objetivo, piel: this.answers.piel, nivel: this.answers.nivel },
        { answers: this.answers, step: QUESTIONS.length });
      const msgs = [
        'Leyendo tu tipo de piel…',
        'Cruzando tu objetivo con ' + (this.cfg.catalog_count || '138') + ' productos…',
        'Ajustando las concentraciones a tu piel…',
        'Armando tu rutina de mañana y noche…',
      ];
      this.paint(`
        <div class="sq-window sq-analysis">
          <div class="sq-window__bar"><span class="sq-dot"></span><span class="sq-dot"></span><span class="sq-dot"></span>
            <span class="sq-window__name">analizando…</span></div>
          <div class="sq-window__body">
            <div class="sq-analysis__ring"><span></span></div>
            <p class="sq-analysis__msg" data-analysis-msg>${esc(msgs[0])}</p>
            <div class="sq-progress sq-progress--thin"><div class="sq-progress__fill" data-analysis-bar style="width:6%"></div></div>
          </div>
        </div>
      `);

      const msgEl = this.querySelector('[data-analysis-msg]');
      const barEl = this.querySelector('[data-analysis-bar]');
      let i = 0;
      const timer = setInterval(() => {
        i += 1;
        if (msgEl && msgs[i]) msgEl.textContent = msgs[i];
        if (barEl) barEl.style.width = Math.min(96, 6 + i * 26) + '%';
      }, 700);

      const [routine] = await Promise.all([
        buildRoutine(this.answers),
        new Promise((r) => setTimeout(r, 2600)),
      ]);
      clearInterval(timer);
      this.routine = routine;

      if (this.cfg.gate_mode === 'after' || this.hasLead()) this.showResults();
      else this.renderGate();
    }

    /* ---------- captura de datos ---------- */

    /** Ya dejó su WhatsApp o su correo. */
    hasLead() {
      return Boolean(this.lead && (this.lead.email || this.lead.telefono || this.lead.desdeWhatsapp));
    }

    renderGate() {
      const c = this.cfg;
      const wa = this.canal === 'whatsapp';
      const count = this.routine ? this.routine.length : 0;
      this.paint(`
        <div class="sq-window sq-gate">
          <div class="sq-window__bar"><span class="sq-dot"></span><span class="sq-dot"></span><span class="sq-dot"></span>
            <span class="sq-window__name">tu-rutina.pdf</span></div>
          <div class="sq-window__body">
            <span class="sq-badge">✓ Tu rutina está lista</span>
            <h3 class="sq-gate__title">${c.gate_heading}</h3>
            <p class="sq-gate__lead">${esc(c.gate_subheading).replace('{n}', count)}</p>
            <ul class="sq-checks sq-checks--tight">
              ${c.gate_bullets.map((b) => `<li>${esc(b)}</li>`).join('')}
            </ul>
            <div class="sq-fields">
              ${c.collect_name ? `<input class="sq-input" type="text" name="nombre" placeholder="Tu nombre" autocomplete="given-name" data-field="nombre">` : ''}
              ${wa ? `
              <input class="sq-input" type="tel" name="telefono" placeholder="Tu WhatsApp (10 dígitos)" autocomplete="tel" inputmode="tel" required data-field="telefono">` : `
              <input class="sq-input" type="email" name="email" placeholder="tucorreo@ejemplo.com" autocomplete="email" inputmode="email" required data-field="email">
              ${c.collect_phone ? `<input class="sq-input" type="tel" name="telefono" placeholder="WhatsApp (opcional)" autocomplete="tel" inputmode="tel" data-field="telefono">` : ''}`}
            </div>
            ${!wa && c.collect_phone && c.wa_optin_label ? `
              <label class="sq-optin">
                <input type="checkbox" data-field="wa-optin">
                <span>${esc(c.wa_optin_label)}</span>
              </label>` : ''}
            <p class="sq-error" data-gate-error hidden></p>
            <button class="sq-btn sq-btn--primary sq-btn--wide" data-action="submit-lead">${esc(c.gate_cta)}</button>
            ${wa && c.wa_consent_note ? `<p class="sq-microcopy">${esc(c.wa_consent_note)}</p>` : ''}
            ${c.gate_mode === 'skippable' ? `<button class="sq-link sq-link--muted" data-action="skip-gate">Ver mi rutina sin dejar mi ${wa ? 'WhatsApp' : 'correo'}</button>` : ''}
            <p class="sq-microcopy">${esc(c.privacy_note)}</p>
          </div>
        </div>
      `);
      this.enterEnvia();
    }

    /** Enter en el campo principal manda el formulario. */
    enterEnvia() {
      const input = this.querySelector(this.canal === 'whatsapp' ? '[data-field="telefono"]' : '[data-field="email"]');
      if (input) input.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.submitLead(); });
    }

    async submitLead() {
      const get = (f) => { const el = this.querySelector(`[data-field="${f}"]`); return el ? el.value.trim() : ''; };
      const wa = this.canal === 'whatsapp';
      const email = get('email');
      const telefono = get('telefono');
      const err = this.querySelector('[data-gate-error]');
      const valido = wa ? telefonoValido(telefono) : /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email);
      if (!valido) {
        if (err) {
          err.textContent = wa
            ? 'Escribe tu WhatsApp a 10 dígitos para mandarte tu rutina.'
            : 'Escribe un correo válido para enviarte tu rutina.';
          err.hidden = false;
        }
        return;
      }
      if (err) err.hidden = true;

      const btn = this.querySelector('[data-action="submit-lead"]');
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }

      /* En modo WhatsApp el consentimiento es el formulario mismo: pide el número
         para mandar la rutina por WhatsApp y el aviso de debajo del botón nombra
         a SkinTalk. En modo correo sigue siendo la casilla aparte. */
      const check = this.querySelector('[data-field="wa-optin"]');
      const optInWhatsapp = wa || Boolean(check && check.checked && telefono);

      this.lead = { email: email || null, nombre: get('nombre'), telefono: telefono || null, optInWhatsapp: optInWhatsapp };
      this.save();
      /* Si aún no hay rutina (captura antes del resultado), se calcula al mostrarla. */
      if (!this.routine) this.routine = await buildRoutine(this.answers);

      /* El alta va a NUESTRO servidor, que es quien crea al cliente en Shopify,
         guarda la rutina y la manda por WhatsApp (o por correo).
         (Antes esto iba directo a Mailchimp por JSONP; ese endpoint dejó de
         funcionar desde el navegador y las altas se perdían en silencio.) */
      track('quiz:lead', wa ? { canal: 'whatsapp' } : { canal: 'email', email_domain: email.split('@')[1] }, {
        answers: this.answers,
        step: QUESTIONS.length,
        lead: {
          email: email || null,
          nombre: get('nombre') || null,
          telefono: telefono || null,
          optInWhatsapp: optInWhatsapp,
          canal: this.canal,
        },
      });

      /* Alta como cliente de Shopify: se conserva como red de seguridad para
         cuando la app no esté configurada todavía (solo existe en modo correo). */
      if (!APP_URL && email) await this.pushShopify();
      this.showResults();
    }

    leadTags() {
      const a = this.answers;
      return ['newsletter', 'quiz', 'quiz-' + a.objetivo, 'piel-' + a.piel, 'nivel-' + a.nivel, 'edad-' + a.edad]
        .filter(Boolean).join(', ');
    }

    /**
     * Shopify protege los formularios de cliente con hCaptcha: si el envío llega
     * sin token, responde «Missing CAPTCHA token» y el alta se pierde. El token
     * solo se genera si se pide explícitamente, porque ni `fetch` ni
     * `form.submit()` disparan el evento que el script de Shopify escucha.
     * Si el captcha no contesta en 5 segundos (bloqueado, sin red, o la tienda
     * lo tiene apagado), seguimos de largo: más vale intentar el alta que dejar
     * a la persona esperando su rutina.
     */
    captchaReady() {
      const captcha = window.Shopify && window.Shopify.captcha;
      if (!captcha || typeof captcha.protect !== 'function') return Promise.resolve();
      return new Promise((resolve) => {
        let done = false;
        const finish = () => { if (done) return; done = true; resolve(); };
        setTimeout(finish, 5000);
        try { captcha.protect(this.form, finish); } catch (e) { finish(); }
      });
    }

    /** Alta como cliente de Shopify (de ahí lo levanta Mailchimp con sus tags). */
    async pushShopify() {
      if (!this.form) return;
      const setVal = (name, value) => {
        const el = this.form.querySelector(`[name="${name}"]`);
        if (el) el.value = value;
      };
      setVal('contact[email]', this.lead.email);
      setVal('contact[first_name]', this.lead.nombre || '');
      setVal('contact[tags]', this.leadTags());

      await this.captchaReady();

      try {
        const res = await fetch(this.form.action, { method: 'POST', body: new FormData(this.form) });
        if (!res.ok) throw new Error('shopify form');
      } catch (e) {
        /* Último recurso: envío real del formulario. Al volver, customer_posted=true
           reconstruye los resultados desde localStorage. */
        try { this.form.submit(); } catch (e2) { /* noop */ }
      }
    }

    /** Alta directa en Mailchimp vía formulario embebido (JSONP, sin backend). */
    pushMailchimp() {
      const url = this.cfg.mailchimp_url;
      if (!url) return Promise.resolve();

      const a = this.answers;
      const params = new URLSearchParams();
      params.set('EMAIL', this.lead.email);
      if (this.lead.nombre) params.set('FNAME', this.lead.nombre);
      if (this.lead.telefono) params.set('PHONE', this.lead.telefono);
      params.set('PIEL', PERFIL.piel[a.piel] || a.piel || '');
      params.set('OBJETIVO', PERFIL.objetivo[a.objetivo] || a.objetivo || '');
      params.set('NIVEL', PERFIL.nivel[a.nivel] || a.nivel || '');
      params.set('EDAD', a.edad || '');
      params.set('RUTINA', (this.routine || []).map((s) => s.options[s.index].title).join(', ').slice(0, 250));
      params.set('QUIZURL', this.shareUrl());

      /* honeypot anti-bot que exige Mailchimp */
      const u = url.match(/[?&]u=([^&]+)/);
      const id = url.match(/[?&]id=([^&]+)/);
      if (u && id) params.set('b_' + u[1] + '_' + id[1], '');

      const endpoint = url.replace('/post?', '/post-json?').replace('/post&', '/post-json&');
      const cb = 'stmc_' + Date.now();

      return new Promise((resolve) => {
        const done = () => { delete window[cb]; script.remove(); resolve(); };
        window[cb] = done;
        const script = document.createElement('script');
        script.src = endpoint + (endpoint.indexOf('?') === -1 ? '?' : '&') + params.toString() + '&c=' + cb;
        script.onerror = done;
        document.head.appendChild(script);
        setTimeout(done, 4000);
      });
    }

    shareUrl() {
      const base = window.location.origin + window.location.pathname;
      return base + '?rutina=' + encodeAnswers(this.answers);
    }

    /* ---------- resultados ---------- */
    async showResults(needsBuild) {
      if (needsBuild || !this.routine) {
        this.paint(`<div class="sq-window sq-analysis"><div class="sq-window__bar"><span class="sq-dot"></span><span class="sq-dot"></span><span class="sq-dot"></span><span class="sq-window__name">cargando tu rutina…</span></div><div class="sq-window__body"><div class="sq-analysis__ring"><span></span></div><p class="sq-analysis__msg">Recuperando tu rutina…</p></div></div>`);
        this.routine = await buildRoutine(this.answers);
      }
      this.save();
      this.renderResults();
      track('quiz:results_view', { nivel: this.answers.nivel, total: this.total() });
    }

    total(steps) {
      return (steps || this.routine || []).reduce((s, st) => s + (st.options[st.index] ? st.options[st.index].price : 0), 0);
    }

    /** Los 4 pasos base, por si el kit completo se le hace mucho. */
    essentials() {
      return BASE.map((k) => (this.routine || []).find((s) => s.key === k)).filter(Boolean);
    }

    /**
     * "Girl math": cuánto cuesta la rutina por día y con qué gasto cotidiano
     * se compara. Solo se muestra una comparación si es CIERTA — si la rutina
     * no le gana a nada, se ofrece bajar de nivel en vez de inventar.
     */
    girlMath(steps) {
      const lista = steps || this.routine || [];
      if (!lista.length) return null;
      const prods = lista.map((s) => s.options[s.index]);
      const dia = prods.reduce((t, prod) => t + porDia(prod), 0);
      /* El suplemento es mensual y arrastraría la duración de toda la rutina. */
      const paraDurar = lista.filter((s) => s.key !== 'suplemento').map((s) => s.options[s.index]);
      const dura = Math.min.apply(null, (paraDurar.length ? paraDurar : prods).map(diasDe));

      const comps = (this.cfg.comparaciones || [])
        .map((x) => ({ label: x.label, dia: Number(x.precio) / Math.max(1, Number(x.cada_dias)) }))
        .filter((x) => x.dia > 0)
        .sort((x, y) => y.dia - x.dia);

      /* La comparación más ajustada que la rutina todavía gana. */
      const gana = comps.filter((x) => x.dia > dia).sort((x, y) => x.dia - y.dia)[0];
      /* Si no gana ninguna, la que quede más cerca por arriba o por abajo. */
      const cerca = comps.sort((x, y) => Math.abs(x.dia - dia) - Math.abs(y.dia - dia))[0];

      return {
        dia: dia, dura: dura,
        mensaje: gana
          ? 'Menos que ' + gana.label + '.'
          : cerca && Math.abs(cerca.dia - dia) / dia < 0.35
            ? 'Lo mismo que ' + cerca.label + ', solo que esto trabaja los ' + dura + ' días.'
            : '',
      };
    }

    renderResults() {
      const c = this.cfg;
      const a = profileOf(this.answers);
      const nombre = this.lead && this.lead.nombre ? this.lead.nombre.split(' ')[0] : '';
      const total = this.total();
      const pct = Number(c.discount_pct) || 0;
      const saving = Math.round(total * pct / 100);
      const finalTotal = total - saving;
      const warns = warnings(a);
      const essentials = this.essentials();
      const essentialTotal = this.total(essentials);
      const gm = this.girlMath();

      const chips = [PERFIL.piel[a.piel], PERFIL.objetivo[a.objetivo], PERFIL.experiencia[a.experiencia], PERFIL.nivel[a.nivel]]
        .filter(Boolean).map((t) => `<span class="sq-chip">${esc(t)}</span>`).join('');

      const stepsFor = (moment) => this.routine
        .filter((s) => STEPS[s.key][moment])
        .sort((x, y) => STEPS[x.key][moment] - STEPS[y.key][moment]);

      const card = (s, i) => {
        const p = s.options[s.index];
        const hasAlt = s.options.length > 1;
        const compare = p.compare_at_price && p.compare_at_price > p.price;
        return `
          <article class="sq-step${s.hero ? ' sq-step--hero' : ''}" data-step="${esc(s.key)}">
            <div class="sq-step__num">${i + 1}</div>
            <div class="sq-step__main">
              <div class="sq-step__meta">
                <span class="sq-step__label">${esc(s.label)}</span>
                <span class="sq-step__freq">${esc(s.freq)}</span>
              </div>
              <div class="sq-product">
                <a class="sq-product__img" href="${STORE}/products/${esc(p.handle)}" target="_blank" rel="noopener">
                  <img src="${esc(imgUrl(p.featured_image, 240))}" alt="${esc(p.title)}" loading="lazy" width="88" height="88">
                </a>
                <div class="sq-product__info">
                  <span class="sq-product__vendor">${esc(p.vendor)}</span>
                  <a class="sq-product__title" href="${STORE}/products/${esc(p.handle)}" target="_blank" rel="noopener">${esc(p.title)}</a>
                  <div class="sq-product__price">
                    <strong>${money(p.price)}</strong>
                    ${compare ? `<s>${money(p.compare_at_price)}</s>` : ''}
                  </div>
                  <span class="sq-product__dura">Te dura ${esc(mesesTexto(diasDe(p)))} · ${money(Math.round(porDia(p)))} al día</span>
                </div>
                <button class="sq-add" data-action="add-one" data-variant="${p.variants.filter((v) => v.available)[0].id}" aria-label="Agregar ${esc(p.title)} al carrito">Agregar</button>
              </div>
              <p class="sq-step__why"><strong>Por qué para ti:</strong> ${s.why}</p>
              ${hasAlt ? `<button class="sq-link sq-link--small" data-action="swap">Ver otra opción (${s.index + 1}/${s.options.length})</button>` : ''}
            </div>
          </article>`;
      };

      this.paint(`
        <div class="sq-results">
          <header class="sq-results__head">
            <span class="sq-badge">Diagnóstico completado</span>
            <h2 class="sq-results__title">${nombre ? esc(nombre) + ', esta' : 'Esta'} es <em>tu</em> rutina</h2>
            <div class="sq-chips">${chips}</div>
          </header>

          <section class="sq-diagnosis">
            ${c.expert_image ? `<img class="sq-diagnosis__avatar" src="${esc(c.expert_image)}" alt="${esc(c.expert_name)}" loading="lazy">` : ''}
            <div>
              <span class="sq-diagnosis__who">${esc(c.expert_name)}</span>
              <p class="sq-diagnosis__text">${DIAGNOSTICO[a.objetivo] || ''}</p>
              ${warns.length ? `<ul class="sq-notes">${warns.map((w) => `<li>${w}</li>`).join('')}</ul>` : ''}
            </div>
          </section>

          <div class="sq-tabs" role="tablist">
            <button class="sq-tab is-active" data-moment="am" role="tab" aria-selected="true">☀️ Mañana</button>
            <button class="sq-tab" data-moment="pm" role="tab" aria-selected="false">🌙 Noche</button>
          </div>

          <div class="sq-folder">
            <div class="sq-folder__tab" data-folder-name>rutina-de-mañana.folder</div>
            <div class="sq-panel is-active" data-panel="am">${stepsFor('am').map(card).join('')}</div>
            <div class="sq-panel" data-panel="pm" hidden>${stepsFor('pm').map(card).join('')}</div>
          </div>

          ${!this.hasLead() ? `
            <section class="sq-inline-capture">
              <div>
                <span class="sq-eyebrow">No la pierdas</span>
                <h3 class="sq-inline-capture__title">Te mandamos tu rutina por ${this.canal === 'whatsapp' ? 'WhatsApp' : 'correo'}</h3>
                <p class="sq-inline-capture__lead">${esc(c.privacy_note)}</p>
              </div>
              <div class="sq-inline-capture__form">
                ${c.collect_name ? `<input class="sq-input" type="text" placeholder="Tu nombre" autocomplete="given-name" data-field="nombre">` : ''}
                ${this.canal === 'whatsapp'
                  ? `<input class="sq-input" type="tel" placeholder="Tu WhatsApp (10 dígitos)" autocomplete="tel" inputmode="tel" data-field="telefono">`
                  : `<input class="sq-input" type="email" placeholder="tucorreo@ejemplo.com" autocomplete="email" inputmode="email" data-field="email">`}
                <button class="sq-btn sq-btn--primary" data-action="submit-lead">Enviármela</button>
                <p class="sq-error" data-gate-error hidden></p>
                ${this.canal === 'whatsapp' && c.wa_consent_note ? `<p class="sq-microcopy">${esc(c.wa_consent_note)}</p>` : ''}
              </div>
            </section>` : ''}

          <section class="sq-kit">
            <div class="sq-kit__copy">
              <span class="sq-eyebrow">Tu kit completo</span>
              <h3 class="sq-kit__title">${this.routine.length} productos, una sola rutina</h3>
              <p class="sq-kit__lead">${esc(c.kit_note)}</p>
              <ul class="sq-kit__trust">
                ${c.trust_items.map((t) => `<li>${esc(t)}</li>`).join('')}
              </ul>
            </div>
            <div class="sq-kit__box">
              <div class="sq-kit__row"><span>Precio de los ${this.routine.length} productos</span><span>${money(total)}</span></div>
              ${saving ? `<div class="sq-kit__row sq-kit__row--save"><span>Descuento de tu diagnóstico (${pct}%)</span><span>−${money(saving)}</span></div>` : ''}
              <div class="sq-kit__row sq-kit__row--total"><span>Tu total</span><span>${money(finalTotal)}</span></div>
              ${gm && gm.mensaje ? `
                <div class="sq-math">
                  <div class="sq-math__big">
                    <strong>${money(Math.round(gm.dia))}</strong>
                    <span>al día</span>
                  </div>
                  <p class="sq-math__detail">Te dura ${esc(mesesTexto(gm.dura))}: no es un gasto de hoy, es lo que cuesta tu piel cada día.</p>
                  <p class="sq-math__hook">${esc(gm.mensaje)}</p>
                </div>` : gm ? `
                <div class="sq-math sq-math--soft">
                  <p class="sq-math__detail">No es un gasto de una vez: esta rutina te dura ${esc(mesesTexto(gm.dura))}.</p>
                </div>` : ''}
              <button class="sq-btn sq-btn--primary sq-btn--wide" data-action="add-all">
                Agregar mi rutina completa
              </button>
              ${c.discount_code ? `<p class="sq-kit__code">Tu código <strong>${esc(c.discount_code)}</strong> se aplica solo al llegar al carrito</p>` : ''}
              ${this.routine.length > essentials.length && essentials.length ? `
                <div class="sq-starter">
                  <p class="sq-starter__lead">¿Prefieres empezar por lo básico?</p>
                  <p class="sq-starter__list">${essentials.map((s) => esc(s.label)).join(' · ')}</p>
                  <button class="sq-btn sq-btn--ghost sq-btn--wide" data-action="add-essentials">
                    Llevarme solo la base · ${money(essentialTotal - Math.round(essentialTotal * pct / 100))}
                  </button>
                  <p class="sq-starter__note">Los 4 pasos que toda piel necesita. El tratamiento lo agregas cuando quieras.</p>
                </div>` : ''}
              <button class="sq-link sq-link--small" data-action="restart">Volver a hacer el diagnóstico</button>
            </div>
          </section>

          <section class="sq-after">
            ${c.whatsapp_number ? `<a class="sq-after__card" href="${esc(this.waLink())}" target="_blank" rel="noopener" data-action="whatsapp">
              <strong>¿Dudas antes de comprar?</strong>
              <span>Escríbele a una especialista por WhatsApp con tu diagnóstico ya cargado.</span>
            </a>` : ''}
            <button class="sq-after__card" data-action="copy-link">
              <strong>Guarda tu rutina</strong>
              <span>Copia el enlace de tus resultados para volver cuando quieras.</span>
            </button>
          </section>
        </div>

        <div class="sq-sticky" data-sticky hidden>
          <div class="sq-sticky__info"><span>Tu rutina · ${this.routine.length} productos</span><strong>${money(finalTotal)}</strong></div>
          <button class="sq-btn sq-btn--primary" data-action="add-all">Agregar todo</button>
        </div>
      `);

      this.setMoment(this.moment);
      this.initSticky();
    }

    waLink() {
      const a = profileOf(this.answers);
      const prods = (this.routine || []).map((s) => '• ' + s.options[s.index].title).join('\n');
      const msg = `Hola, acabo de hacer el diagnóstico de piel en SkinTalk.\n\nMi perfil: ${PERFIL.piel[a.piel]}, objetivo ${PERFIL.objetivo[a.objetivo]}, ${PERFIL.experiencia[a.experiencia]}.\n\nMi rutina:\n${prods}\n\nMe gustaría una recomendación antes de comprar.`;
      return 'https://wa.me/' + String(this.cfg.whatsapp_number).replace(/\D/g, '') + '?text=' + encodeURIComponent(msg);
    }

    /** La barra fija aparece cuando ya pasaste el encabezado y aún no llegas al kit. */
    initSticky() {
      const bar = this.querySelector('[data-sticky]');
      const head = this.querySelector('.sq-results__head');
      const kit = this.querySelector('.sq-kit');
      if (!bar || !head || !kit || !('IntersectionObserver' in window)) return;

      const state = { headVisible: true, kitVisible: false };
      const sync = () => { bar.hidden = state.headVisible || state.kitVisible; };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.target === head) state.headVisible = e.isIntersecting;
          if (e.target === kit) state.kitVisible = e.isIntersecting;
        });
        sync();
      }, { rootMargin: '-70px 0px -20px 0px' });
      io.observe(head);
      io.observe(kit);
      this._stickyObserver = io;
    }

    setMoment(moment) {
      this.moment = moment;
      this.querySelectorAll('.sq-tab').forEach((t) => {
        const on = t.dataset.moment === moment;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.querySelectorAll('.sq-panel').forEach((p) => {
        const on = p.dataset.panel === moment;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      });
      const name = this.querySelector('[data-folder-name]');
      if (name) name.textContent = moment === 'am' ? 'rutina-de-mañana.folder' : 'rutina-de-noche.folder';
    }

    /* ---------- carrito ---------- */
    async addToCart(items, btn) {
      if (!items.length) return;

      /* Fuera de Shopify no hay /cart/add.js: se manda a la tienda. Un solo
         producto entra a su carrito; la rutina completa usa la liga de carrito
         de Shopify, que la arma y la lleva directo a pagar. */
      if (STORE) {
        track('quiz:add_to_cart', { count: items.length, value: this.total() / 100, codigo: this.codigo || undefined });
        const qs = new URLSearchParams();
        if (this.cfg.discount_code) qs.set('discount', this.cfg.discount_code);
        const url = items.length === 1
          ? STORE + '/cart/add?id=' + items[0].id + '&quantity=' + items[0].quantity
          : STORE + '/cart/' + items.map((i) => i.id + ':' + i.quantity).join(',') + (qs.toString() ? '?' + qs : '');
        if (btn) { btn.disabled = true; btn.textContent = 'Abriendo la tienda…'; }
        setTimeout(() => { window.location.href = url; }, 150); // deja salir el beacon
        return;
      }

      const label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); btn.textContent = 'Agregando…'; }
      try {
        const config = window.SkinTalk && SkinTalk.fetchConfig ? SkinTalk.fetchConfig()
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
        config.body = JSON.stringify({ items: items });
        const res = await fetch('/cart/add.js', config);
        if (!res.ok) throw new Error('cart');

        track('quiz:add_to_cart', { count: items.length, value: this.total() / 100, codigo: this.codigo || undefined });

        /* Si vino de su liga de WhatsApp, la orden dice de qué rutina salió. */
        if (this.codigo) {
          try {
            await fetch('/cart/update.js', Object.assign({}, config, { body: JSON.stringify({ attributes: { rutina: this.codigo } }) }));
          } catch (e) { /* no bloquea la compra */ }
        }

        if (items.length > 1 && this.cfg.discount_code) {
          window.location.href = '/discount/' + encodeURIComponent(this.cfg.discount_code) + '?redirect=/cart';
          return;
        }
        const cart = await (await fetch('/cart.js')).json();
        if (window.SkinTalk) { SkinTalk.publish('cart:updated', cart); SkinTalk.publish('product:added', { source: 'skin-quiz' }); }
        if (btn) { btn.textContent = '✓ Agregado'; btn.classList.add('is-done'); }
      } catch (e) {
        if (btn) btn.textContent = 'Intenta de nuevo';
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          setTimeout(() => { if (btn.classList.contains('is-done')) { btn.textContent = label; btn.classList.remove('is-done'); } }, 2200);
        }
      }
    }

    /* ---------- eventos ---------- */
    onKeydown(e) {
      const opt = e.target.closest ? e.target.closest('.sq-option') : null;
      if (opt && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        this.answer(opt.dataset.value);
      }
    }

    onClick(e) {
      const opt = e.target.closest('.sq-option');
      if (opt) { this.answer(opt.dataset.value); return; }

      const tab = e.target.closest('.sq-tab');
      if (tab) { this.setMoment(tab.dataset.moment); return; }

      const el = e.target.closest('[data-action]');
      if (!el) return;
      const action = el.dataset.action;

      if (action === 'start') { this.step = 0; track('quiz:start', {}, { step: 0 }); this.renderQuestion(); }
      else if (action === 'resume') { const s = this.loadSaved(); this.answers = s.answers; this.lead = s.lead; this.showResults(true); }
      else if (action === 'back') { this.step = Math.max(0, this.step - 1); this.renderQuestion(); }
      else if (action === 'next') { this.next(); }
      else if (action === 'toggle-note') { this.toggleNote(el.dataset.note); }
      else if (action === 'submit-lead') { this.submitLead(); }
      else if (action === 'skip-gate') { track('quiz:gate_skipped'); this.showResults(); }
      else if (action === 'restart') { this.answers = {}; this.routine = null; this.step = 0; this.renderQuestion(); }
      else if (action === 'copy-link') { this.copyLink(el); }
      else if (action === 'whatsapp') { track('quiz:whatsapp'); }
      else if (action === 'swap') { this.swap(el); }
      else if (action === 'add-one') { this.addToCart([{ id: Number(el.dataset.variant), quantity: 1 }], el); }
      else if (action === 'add-all' || action === 'add-essentials') {
        const steps = action === 'add-all' ? this.routine : this.essentials();
        const items = steps.map((s) => {
          const v = s.options[s.index].variants.filter((x) => x.available)[0];
          return v ? { id: v.id, quantity: 1 } : null;
        }).filter(Boolean);
        track('quiz:' + action, { count: items.length });
        this.addToCart(items, el);
      }
    }

    swap(btn) {
      const card = btn.closest('.sq-step');
      const key = card.dataset.step;
      const s = this.routine.find((x) => x.key === key);
      if (!s) return;
      s.index = (s.index + 1) % s.options.length;
      track('quiz:swap', { step: key, product: s.options[s.index].handle });
      this.renderResults();
    }

    copyLink(btn) {
      const url = this.shareUrl();
      const done = () => {
        const span = btn.querySelector('span');
        if (span) { const old = span.textContent; span.textContent = '¡Enlace copiado! Pégalo donde quieras.'; setTimeout(() => { span.textContent = old; }, 2500); }
      };
      if (navigator.clipboard) navigator.clipboard.writeText(url).then(done).catch(done);
      else { window.prompt('Copia tu enlace:', url); }
    }
  }

  if (!customElements.get('skin-quiz')) customElements.define('skin-quiz', SkinQuiz);

  /* Solo para pruebas fuera del navegador (node). En el tema no hace nada. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      QUESTIONS, ANSWER_KEYS, BASE, BLUEPRINT, STEPS, RULES, RULE_LABELS, SAFE_FALLBACK,
      DIAS, DIAS_DEFAULT, diasDe, porDia, mesesTexto,
      EMBARAZO_EXCLUIR, EXPERIENCIA_POR_USO, DIAGNOSTICO, PERFIL,
      profileOf, candidatesFor, buildRoutine, warnings, freqFor,
      decodeAnswers, encodeAnswers,
    };
  }
})();
